<script setup lang="ts">
import { computed, ref } from "vue";
import type { SuccessTicket } from "@/types";

const props = defineProps<{
  tickets: SuccessTicket[];
  orderId?: string;
  totalQuantity: number;
  totalPrice: number;
  email: string;
  formatPrice: (value: number) => string;
}>();

const emit = defineEmits<{
  returnTicket: [ticketId: string];
}>();

const ticketToReturnId = ref<string | null>(null);

const ticketToReturn = computed(
  () =>
    props.tickets.find((ticket) => ticket.id === ticketToReturnId.value) ??
    null,
);

const ticketToReturnStatus = computed(
  () => ticketToReturn.value?.returnStatus ?? "idle",
);

function formatTicketQuantity(value: number) {
  const mod10 = value % 10;
  const mod100 = value % 100;

  if (mod10 === 1 && mod100 !== 11) return `${value} билет`;
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    return `${value} билета`;
  }

  return `${value} билетов`;
}

function openReturnModal(ticket: SuccessTicket) {
  ticketToReturnId.value = ticket.id;
}

function closeReturnModal() {
  if (ticketToReturnStatus.value === "loading") {
    return;
  }

  ticketToReturnId.value = null;
}

function confirmReturnTicket() {
  if (!ticketToReturn.value || ticketToReturnStatus.value === "loading") {
    return;
  }

  emit("returnTicket", ticketToReturn.value.id);
}

function getReturnButtonLabel(ticket: SuccessTicket) {
  if (ticket.returnStatus === "loading") return "Возвращаем...";
  if (ticket.returnStatus === "success") return "Билет возвращён";

  return "Вернуть билет";
}

function isReturnButtonDisabled(ticket: SuccessTicket) {
  return ticket.returnStatus === "loading" || ticket.returnStatus === "success";
}

function isTicketCancelled(ticket: SuccessTicket) {
  return (
    ticket.status?.toLocaleLowerCase("en-US") === "cancelled" ||
    ticket.returnStatus === "success"
  );
}

function getModalTitle() {
  if (ticketToReturnStatus.value === "success") return "Билет возвращён";
  if (ticketToReturnStatus.value === "error") return "Не удалось вернуть билет";
  if (ticketToReturnStatus.value === "loading") return "Возвращаем билет";

  return "Вернуть билет?";
}

function getModalMessage() {
  if (ticketToReturnStatus.value === "success") {
    return ticketToReturn.value?.returnMessage || "Возврат успешно оформлен.";
  }

  if (ticketToReturnStatus.value === "error") {
    return "Попробуйте повторить возврат позже.";
  }

  if (ticketToReturnStatus.value === "loading") {
    return "Отправляем запрос на возврат билета.";
  }

  return "После подтверждения билет будет отправлен на возврат.";
}
</script>

<template>
  <section
    class="grid w-full items-start xl:grid-cols-[minmax(0,1020px)_500px] gap-5 [&>*]:min-w-0"
  >
    <section
      class="order-2 grid gap-1 lg:gap-2 overflow-hidden rounded-[40px] bg-darkwhite shadow-widget xl:order-1"
    >
      <article
        v-for="ticket in tickets"
        :key="ticket.id"
        class="grid bg-white p-4 lg:p-7 first:rounded-t-[30px] last:rounded-b-[30px] md:gap-6 md:px-8 md:py-8 lg:gap-8 lg:first:rounded-t-[40px] lg:last:rounded-b-[40px]"
        :class="
          isTicketCancelled(ticket)
            ? 'md:grid-cols-1'
            : 'md:grid-cols-[minmax(0,1fr)_120px]'
        "
      >
        <div
          class="grid min-w-0 gap-x-5 md:block"
          :class="
            isTicketCancelled(ticket)
              ? 'grid-cols-1'
              : 'grid-cols-[minmax(0,1fr)_120px]'
          "
        >
          <p
            class="min-w-0 text-sm lg:text-base font-medium leading-[18px] lg:leading-5 text-ink/50 [overflow-wrap:anywhere]"
            :class="isTicketCancelled(ticket) ? 'col-span-1' : 'col-span-2'"
          >
            {{ ticket.subtitle }}
          </p>
          <div
            class="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2"
            :class="isTicketCancelled(ticket) ? 'col-span-1' : 'col-span-2'"
          >
            <h2
              class="m-0 min-w-0 text-lg lg:text-2xl font-semibold uppercase leading-[22px] lg:leading-7 text-ink [overflow-wrap:anywhere]"
            >
              {{ ticket.title }}
            </h2>
            <span
              v-if="isTicketCancelled(ticket)"
              class="inline-flex h-7 items-center rounded-full border border-darkred/20 bg-darkred/10 px-3 text-xs font-semibold leading-none text-darkred lg:text-sm"
            >
              Билет отменен
            </span>
          </div>
          <div
            class="mt-3 lg:mt-[19px] h-px bg-ink/10"
            :class="isTicketCancelled(ticket) ? 'col-span-1' : 'col-span-2'"
          />

          <div
            class="col-start-1 mt-3 lg:mt-[19px] flex flex-col items-start gap-2 lg:gap-4 md:flex-row md:items-end md:justify-between md:gap-6 lg:gap-8"
          >
            <div class="grid gap-2 lg:flex lg:items-center lg:gap-5">
              <div>
                <p
                  class="text-sm lg:text-base leading-[18px] lg:leading-5 font-medium text-ink/50"
                >
                  Дата
                </p>
                <p
                  class="mt-0.5 lg:mt-2 text-base lg:text-xl leading-5 lg:leading-6 font-medium"
                >
                  {{ ticket.date }}
                </p>
              </div>
              <div>
                <p
                  class="text-sm lg:text-base leading-[18px] lg:leading-5 font-medium text-ink/50"
                >
                  Цена
                </p>
                <p
                  class="mt-0.5 lg:mt-2 text-base lg:text-xl leading-5 lg:leading-6 font-medium"
                >
                  {{ formatPrice(ticket.price) }}
                </p>
              </div>
            </div>
            <div
              v-if="!isTicketCancelled(ticket)"
              class="grid gap-1 lg:justify-items-end lg:justify-self-end"
            >
              <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                <a
                  v-if="ticket.downloadUrl"
                  class="inline-flex cursor-pointer items-center gap-1 lg:gap-2 text-sm lg:text-base font-medium leading-[18px] lg:leading-5 text-brand"
                  :href="ticket.downloadUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Скачать билет
                  <svg class="h-6 w-6 min-w-6" aria-hidden="true">
                    <use href="#ticket-widget-icon-download" />
                  </svg>
                </a>
                <button
                  v-if="ticket.isConfirmed"
                  class="inline-flex cursor-pointer items-center gap-1 lg:gap-2 text-sm lg:text-base font-medium leading-[18px] lg:leading-5 transition disabled:cursor-default"
                  :class="
                    ticket.returnStatus === 'success'
                      ? 'text-brand'
                      : 'text-darkred disabled:text-darkred/60'
                  "
                  type="button"
                  :disabled="isReturnButtonDisabled(ticket)"
                  @click="openReturnModal(ticket)"
                >
                  {{ getReturnButtonLabel(ticket) }}
                  <svg
                    class="h-5 w-5 min-w-5 lg:h-6 lg:w-6 lg:min-w-6"
                    aria-hidden="true"
                  >
                    <use
                      :href="
                        ticket.returnStatus === 'success'
                          ? '#ticket-widget-icon-check'
                          : '#ticket-widget-icon-return'
                      "
                    />
                  </svg>
                </button>
              </div>
              <p
                v-if="ticket.returnStatus === 'error' && ticket.returnMessage"
                class="text-xs lg:text-sm font-medium leading-4 text-darkred"
              >
                {{ ticket.returnMessage }}
              </p>
            </div>
          </div>

          <div
            v-if="!isTicketCancelled(ticket)"
            class="mt-3 col-start-2 row-span-2 row-start-4 grid justify-items-center md:hidden"
          >
            <img
              v-if="ticket.barcodeImage"
              class="h-[100px] w-[100px] object-contain"
              :src="ticket.barcodeImage"
              alt=""
            />
            <svg v-else class="h-[100px] w-[100px]" aria-hidden="true">
              <use href="#ticket-widget-icon-qr" />
            </svg>
            <p
              class="mt-1 max-w-full break-all text-center text-[10px] lg:text-xs font-medium leading-3 lg:leading-4 text-ink/50"
            >
              {{ ticket.number }}
            </p>
          </div>
        </div>

        <div
          v-if="!isTicketCancelled(ticket)"
          class="hidden justify-items-center md:grid"
        >
          <img
            v-if="ticket.barcodeImage"
            class="h-[120px] w-[120px] object-contain"
            :src="ticket.barcodeImage"
            alt=""
          />
          <svg v-else class="h-[120px] w-[120px]" aria-hidden="true">
            <use href="#ticket-widget-icon-qr" />
          </svg>
          <p
            class="mt-1 max-w-[120px] break-all text-center text-xs font-medium leading-4 text-ink/50"
          >
            {{ ticket.number }}
          </p>
        </div>
      </article>
    </section>

    <aside
      class="order-1 rounded-[30px] lg:rounded-[40px] [background:radial-gradient(119.3%_147.28%_at_105.1%_119.52%,rgba(255,246,245,0)_0%,rgba(254,225,211,0.78)_62.95%,rgba(136,232,1,0.4)_100%),linear-gradient(125.99deg,#FFF6F5_-15.51%,#EDD0FF_86.65%)] p-4 lg:p-6 shadow-widget xl:order-2"
    >
      <h2 class="m-0 text-xl lg:text-[32px] leading-6 lg:leading-9 font-medium">
        Детали заказа
      </h2>

      <div class="mt-6 grid gap-3 lg:gap-5 text-base lg:text-lg">
        <div class="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2">
          <span
            class="min-w-0 overflow-hidden leading-5 lg:leading-[22px] after:ml-1.5 after:mr-[-100%] after:inline-block after:w-full after:border-b after:border-dashed after:border-ink/20 after:content-['']"
          >
            Номер заказа
          </span>
          <span
            class="min-w-0 text-right leading-[22px] [overflow-wrap:anywhere]"
          >
            {{ orderId || "765947633446" }}
          </span>
        </div>
        <div class="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2">
          <span
            class="min-w-0 overflow-hidden leading-5 lg:leading-[22px] after:ml-1.5 after:mr-[-100%] after:inline-block after:w-full after:border-b after:border-dashed after:border-ink/20 after:content-['']"
          >
            Билеты
          </span>
          <span class="whitespace-nowrap font-semibold leading-[22px]">
            {{ formatTicketQuantity(totalQuantity) }}
          </span>
        </div>
      </div>

      <div class="mt-6 mb-4 lg:my-6 h-px bg-ink/10" />

      <div class="flex items-center justify-between">
        <span class="text-xl lg:text-2xl leading-6 lg:leading-7 font-semibold"
          >Итого</span
        >
        <span
          class="text-2xl lg:text-4xl leading-7 lg:leading-10 font-semibold"
        >
          {{ formatPrice(totalPrice) }}
        </span>
      </div>

      <div
        class="mt-4 lg:mt-6 flex items-center gap-3 rounded-[20px] bg-darkwhite p-4"
      >
        <span
          class="grid h-10 w-10 min-w-10 shrink-0 place-items-center rounded-full bg-brand text-white"
        >
          <svg class="h-6 w-6 min-w-6 fill-darkwhite" aria-hidden="true">
            <use href="#ticket-widget-icon-info" />
          </svg>
        </span>
        <p class="text-sm lg:text-xl font-medium leading-[18px] lg:leading-6">
          Чек и билеты отправлены на почту
          <span class="text-brand [overflow-wrap:anywhere]">{{ email }}</span>
        </p>
      </div>

    </aside>
  </section>

  <div
    v-if="ticketToReturn"
    class="fixed inset-0 z-[80] grid place-items-center bg-ink/45 px-4 py-8"
    role="dialog"
    aria-modal="true"
    @click.self="closeReturnModal"
  >
    <section
      class="w-full max-w-[460px] rounded-[28px] lg:rounded-[36px] bg-white p-5 lg:p-7 shadow-widget"
    >
      <div class="grid justify-items-center gap-4 text-center">
        <span
          class="grid h-12 w-12 place-items-center rounded-full lg:h-14 lg:w-14"
          :class="
            ticketToReturnStatus === 'success'
              ? 'bg-brand text-white'
              : ticketToReturnStatus === 'error'
                ? 'bg-darkred/10 text-darkred'
                : 'bg-darkred/10 text-darkred'
          "
        >
          <span
            v-if="ticketToReturnStatus === 'loading'"
            class="h-6 w-6 animate-spin rounded-full border-[3px] border-darkred/20 border-t-darkred"
          />
          <svg v-else class="h-6 w-6" aria-hidden="true">
            <use
              :href="
                ticketToReturnStatus === 'success'
                  ? '#ticket-widget-icon-check'
                  : ticketToReturnStatus === 'error'
                    ? '#ticket-widget-icon-close'
                    : '#ticket-widget-icon-return'
              "
            />
          </svg>
        </span>

        <div>
          <h2
            class="m-0 text-xl lg:text-2xl font-semibold leading-6 lg:leading-7 text-ink"
          >
            {{ getModalTitle() }}
          </h2>
          <p
            class="mt-2 text-sm lg:text-base font-medium leading-[18px] lg:leading-5 text-ink/70"
          >
            {{ getModalMessage() }}
          </p>
        </div>

        <div class="w-full rounded-[18px] bg-darkwhite px-4 py-3 text-left">
          <p
            class="text-sm lg:text-base font-semibold uppercase leading-[18px] lg:leading-5 text-ink"
          >
            {{ ticketToReturn.title }}
          </p>
          <p class="mt-1 text-xs lg:text-sm font-medium text-ink/60">
            {{ ticketToReturn.date }} · {{ formatPrice(ticketToReturn.price) }}
          </p>
        </div>

        <div class="grid w-full gap-2">
          <button
            v-if="ticketToReturnStatus === 'success'"
            class="h-11 w-full cursor-pointer rounded-full bg-brand text-base font-semibold text-white transition hover:bg-brand/90"
            type="button"
            @click="closeReturnModal"
          >
            Закрыть
          </button>

          <template v-else>
            <button
              class="h-11 w-full cursor-pointer rounded-full bg-darkred text-base font-semibold text-white transition hover:bg-darkred/90 disabled:cursor-default disabled:bg-darkred/45"
              type="button"
              :disabled="ticketToReturnStatus === 'loading'"
              @click="confirmReturnTicket"
            >
              {{
                ticketToReturnStatus === "loading"
                  ? "Возвращаем..."
                  : ticketToReturnStatus === "error"
                    ? "Попробовать ещё раз"
                    : "Подтвердить возврат"
              }}
            </button>
            <button
              class="h-11 w-full cursor-pointer rounded-full border-[2px] border-solid border-ink/10 bg-transparent text-base font-semibold text-ink transition hover:border-ink/25 disabled:cursor-default disabled:opacity-50"
              type="button"
              :disabled="ticketToReturnStatus === 'loading'"
              @click="closeReturnModal"
            >
              Отмена
            </button>
          </template>
        </div>
      </div>
    </section>
  </div>
</template>
